import api from '../store/apiClient';
import { modamaiTrace } from '../utils/modamaiTrace';

function unwrapEnvelope(body) {
  const payload = body?.data !== undefined ? body.data : body;
  const raw = payload?.products ?? (Array.isArray(payload) ? payload : []);
  return {
    products: Array.isArray(raw) ? raw : [],
    pagination: payload?.pagination ?? null,
  };
}

export const productService = {
  /** @returns {{ products: unknown[], pagination: object|null }} */
  async getProducts(params = {}) {
    modamaiTrace('productService.getProducts request', { params });
    const { data } = await api.get('/products', { params });
    const out = unwrapEnvelope(data);
    modamaiTrace('productService.getProducts response', {
      count: out.products?.length ?? 0,
      pagination: out.pagination,
      topLevelKeys:
        data && typeof data === 'object' ? Object.keys(data).slice(0, 12) : null,
    });
    return out;
  },

  async getFilterOptions(params = {}) {
    const { data } = await api.get('/products/filter-options', { params });
    const payload = data?.data ?? data ?? {};
    return {
      colors: Array.isArray(payload.colors) ? payload.colors : [],
      sizes: Array.isArray(payload.sizes) ? payload.sizes : [],
      price: payload.price ?? null,
      flags: payload.flags ?? null,
    };
  },

  /** Full product payload (single) */
  async getProductBySlugOrId(slugOrId) {
    modamaiTrace('productService.getProductBySlugOrId request', { slugOrId });
    const { data } = await api.get(`/products/by/${encodeURIComponent(slugOrId)}`);
    const body = data?.data ?? data;
    modamaiTrace('productService.getProductBySlugOrId response', {
      slugOrId,
      typeofBody: typeof body,
      keys: body && typeof body === 'object' ? Object.keys(body).slice(0, 20) : null,
    });
    return body;
  },

  async getShippingOptions(productId, params = {}) {
    const { data } = await api.get(`/products/${encodeURIComponent(productId)}/freight`, { params });
    return data?.data || { options: [] };
  },

  async getCartShippingOptions(body = {}) {
    try {
      const { data } = await api.post('/products/shipping-options', body);
      return data?.data || { mode: 'none', options: [], packages: [] };
    } catch (error) {
      const status = error?.response?.status;
      // Backward-compatible fallback for servers not yet deployed with POST /products/shipping-options.
      if (status !== 404) throw error;
      const countryCode = String(body?.countryCode || '').trim();
      const items = Array.isArray(body?.items) ? body.items : [];
      if (!countryCode || items.length === 0) return { mode: 'none', options: [], packages: [] };

      // Try "single_package" behavior using first valid line as baseline (legacy-compatible),
      // then also build split package options so checkout can still proceed deterministically.
      const first = items.find((it) => it?.productId && it?.variantId);
      const splitPackages = [];

      for (let i = 0; i < items.length; i += 1) {
        const it = items[i] || {};
        if (!it.productId || !it.variantId) continue;
        try {
          const { data } = await api.get(`/products/${encodeURIComponent(it.productId)}/freight`, {
            params: {
              countryCode,
              quantity: Number(it.quantity) || 1,
              variantId: it.variantId,
            },
          });
          const payload = data?.data || {};
          splitPackages.push({
            packageId: `pkg-${i + 1}`,
            itemRef: { productId: it.productId, variantId: it.variantId, quantity: Number(it.quantity) || 1 },
            options: Array.isArray(payload.options) ? payload.options : [],
            cheapestOption: payload.cheapestOption || null,
            fastestOption: payload.fastestOption || null,
          });
        } catch {
          // ignore bad line and continue
        }
      }

      if (first) {
        try {
          const { data } = await api.get(`/products/${encodeURIComponent(first.productId)}/freight`, {
            params: {
              countryCode,
              quantity: Number(first.quantity) || 1,
              variantId: first.variantId,
            },
          });
          const payload = data?.data || {};
          const singleOptions = Array.isArray(payload.options) ? payload.options : [];
          if (singleOptions.length > 0) {
            return {
              mode: 'single_package',
              options: singleOptions,
              cheapestOption: payload.cheapestOption || null,
              fastestOption: payload.fastestOption || null,
              packages: [],
              meta: { fallbackMode: 'legacy-first-line' },
            };
          }
        } catch {
          // continue to split mode
        }
      }

      return {
        mode: splitPackages.length > 0 ? 'split_packages' : 'none',
        options: [],
        cheapestOption: null,
        fastestOption: null,
        packages: splitPackages,
        meta: { fallbackMode: 'legacy-split-from-freight' },
      };
    }
  },
};
